<template>
    <csp-desktop-info-card
        :cols="12"
        :form-context="formContext"
        icon="inticon-autoclaim_car"
        title="Fahrzeug"
        @change="reloadVehicleFormData"
        @save="saveVehicleFormData">
        <template v-if="formContext.permissions.canCreateRetroactiveChangesNote === true" v-slot:after-icons>
            <csp-retroactive-changes-modal :record-id="recordId" title="Fahrzeug"/>
        </template>
        <b-tabs align="center" no-fade>
            <b-tab :title-item-class="formContext.isInEditMode ? '' : 'd-none'">
                <template v-slot:title>
                    <span class="tab-title">Manuelle Eingabe</span>
                </template>
                <div class="tab-body">
                    <h2>Grunddaten</h2>
                    <b-row>
                        <b-col cols="3">
                            <csp-desktop-info-card-element
                                :form-context="formContext"
                                label="Kennzeichen"
                                property="licensePlate"
                                type="text">
                                <template v-slot:display-value>
                                    <csp-license-plate
                                        :region="licensePlate.region"
                                        :rest="licensePlate.rest"/>
                                </template>
                            </csp-desktop-info-card-element>
                        </b-col>
                        <b-col cols="3">
                            <csp-desktop-info-card-element
                                :form-context="formContext"
                                label="Typ"
                                :options-loader="loadVehicleCategoryOptions"
                                property="vehicleCategory"
                                :required="true"
                                type="autocomplete"/>
                        </b-col>
                    </b-row>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :dependencies="['vehicleCategory']"
                            :form-context="formContext"
                            label="Hersteller"
                            :options-loader="loadVehicleManufacturerOptions"
                            property="vehicleManufacturer"
                            type="autocomplete"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Modell"
                            property="model"
                            :required="true"
                            type="text"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Erstzulassungsdatum"
                            property="registrationDate"
                            type="datepicker"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Fahrgestellnummer"
                            property="vin"
                            type="text"/>
                    </b-row>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="HSN"
                            property="hsn"
                            type="text"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="TSN"
                            property="tsn"
                            type="text"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Kilometerstand"
                            property="mileage"
                            type="text"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Leistung (KW)"
                            property="kw"
                            type="text"/>
                    </b-row>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :dependencies="['vehicleCategory']"
                            :form-context="formContext"
                            label="Treibstoff"
                            :options-loader="loadVehicleFuelTypeOptions"
                            property="vehicleFuelType"
                            type="autocomplete"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Mietwagenklasse"
                            :options-loader="loadVehicleRentClassOptions"
                            property="rentClass"
                            type="autocomplete"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Fahrzeugart Anlieferung"
                            property="deliveredVehicleType"
                            required
                            type="autocomplete"/>
                    </b-row>
                </div>
            </b-tab>

            <b-tab :title-item-class="formContext.isInEditMode ? '' : 'd-none'">
                <template v-slot:title>
                    <span class="tab-title">DAT Suchbaum</span>
                </template>
                <div class="tab-body">
                    <b-alert show variant="warning">
                        Diese Funktion steht in Kürze wieder zur Verfügung.
                    </b-alert>
                </div>
            </b-tab>

            <b-tab :title-item-class="formContext.isInEditMode ? '' : 'd-none'">
                <template v-slot:title>
                    <span class="tab-title">VIN Abfrage</span>
                </template>
                <div class="tab-body">
                    <b-alert show variant="warning">
                        Diese Funktion steht in Kürze wieder zur Verfügung.
                    </b-alert>
                </div>
            </b-tab>

            <b-tab :title-item-class="formContext.isInEditMode ? '' : 'd-none'">
                <template v-slot:title>
                    <span class="tab-title">HSN/TSN</span>
                </template>
                <div class="tab-body">
                    <b-alert show variant="warning">
                        Diese Funktion steht in Kürze wieder zur Verfügung.
                    </b-alert>
                </div>
            </b-tab>
        </b-tabs>
    </csp-desktop-info-card>
</template>

<script>
    import Container from '@services/Container';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';

    const container = Container.getInstance();

    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'VehicleBasedata',
        components: {
            'csp-license-plate': () => import('@desktop/components/record/vehicle/LicensePlate.vue'),
        },
        mixins: [FormDataMixin],
        provide() {
            return {
                emitFieldChange: (...args) => this.handleFormFieldChange(...args),
            }
        },
        computed: {
            recordId() {
                return parseInt(this.$route.params.recordId);
            },
            licensePlate() {
                return this.formContext.complexFields.licensePlate;
            },
        },
        methods: {
            loadVehicleCategoryOptions() {
                return recordRepository.loadLegacySelectOptions('vehicleCategories');
            },
            loadVehicleManufacturerOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'vehicleManufacturer',
                    {vehicleCategoryId: this.formContext.form.getProperty('vehicleCategory')}
                );
            },
            loadVehicleFuelTypeOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'vehicleFuelType',
                    {vehicleCategoryId: this.formContext.form.getProperty('vehicleCategory')}
                );
            },
            loadVehicleRentClassOptions() {
                return recordRepository.loadLegacySelectOptions('rentClass');
            },
            async saveVehicleFormData(formContext) {
                const saveFunction = (objectId, postData) => {
                    return recordRepository.saveVehicleBasedata(this.recordId, postData);
                }
                this.saveFormData(formContext, saveFunction, null);
            },
            reloadVehicleFormData(formContext) {
                const reloadFunction = (objectId, postData) => {
                    return recordRepository.reloadVehicleBasedata(this.recordId, postData)
                }
                this.reloadFormData(formContext, reloadFunction, null)
            },
        },
    }
</script>
